'use client';

import { AnimatePresence, motion } from 'framer-motion';
import styled from 'styled-components';
import { theme, useMeasure } from '@frameio/vapor';
import { refreshChipAnimation } from './animations';
import { RefreshChipButton } from './RefreshChipButton';
import type { RefreshChipProps } from './types';

type ContainerProps = {
  $anchorBottom?: boolean;
};

const Container = styled.div<ContainerProps>`
  bottom: ${({ $anchorBottom }) => $anchorBottom && theme.spaces[36]};
  left: 50%;
  min-width: 129px;
  position: absolute;
  z-index: ${theme.zIndices.panel + 1};
`;

export function RefreshChip({
  anchorBottom = false,
  copy,
  count,
  dataTestId,
  iconAfter,
  iconBefore,
  label,
  motionKey,
  onClick,
  size = 'small',
  visible,
}: RefreshChipProps) {
  const [refForMeasure, { width: targetWidth }] = useMeasure();
  const chipAnimation = refreshChipAnimation({ targetWidth });

  return (
    <Container
      $anchorBottom={anchorBottom}
      aria-atomic="true"
      aria-live="polite"
      role="status"
    >
      {/* Hidden reference used to measure and animate the width when the copy changes.
        Only measure when `count` is positive to avoid rendering artifacts when count is 0
        and the chip animates out. */}
      {count > 0 && (
        <div ref={refForMeasure} style={{ height: '0', visibility: 'hidden' }}>
          <RefreshChipButton
            copy={copy}
            count={count}
            iconAfter={iconAfter ?? null}
            iconBefore={iconBefore ?? null}
            label={label}
            size={size}
          />
        </div>
      )}
      <AnimatePresence initial={false}>
        {visible && (
          <motion.div {...chipAnimation} key={motionKey}>
            <RefreshChipButton
              copy={copy}
              count={count}
              dataTestId={dataTestId}
              iconAfter={iconAfter ?? null}
              iconBefore={iconBefore ?? null}
              label={label}
              onClick={onClick}
              size={size}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </Container>
  );
}
